import {
  ComposerPrimitive,
  unstable_defaultDirectiveFormatter,
  unstable_useMentionAdapter,
  unstable_useSlashCommandAdapter,
  useAuiState,
} from '@assistant-ui/react'
import { ComposerAddAttachment, ComposerAttachments } from '@/components/assistant-ui/attachment.aui'
import { ComposerTriggerPopover } from '@/components/assistant-ui/composer-trigger-popover.aui'
import { buttonVariants } from '@/components/shadcn/button'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconV2ArrowLeftOutline } from '@icons/IconV2ArrowLeftOutline'
import { useCallback, useMemo, useState, type RefObject } from 'react'
import {
  buildDmpChatMentionCategories,
  DMP_CHAT_SLASH_COMMANDS,
  formatDmpChatSlashHelp,
  getDmpChatSlashIntent,
} from '../dmpChatComposerTriggers'
import { useDmpChatAgent } from './DmpChatAgentContext'
import { DmpChatContextUsage } from './DmpChatContextUsage'
import { DmpChatLexicalInput, type DmpChatComposerInputHandle } from './DmpChatLexicalInput'
import { useDmpChatRuntime } from './DmpChatRuntimeProvider'
import { DmpChatDirectiveChip, dmpChatTriggerFallbackIcon, dmpChatTriggerIconMap } from './DmpChatTriggerPresentation'
import clsx from 'clsx'

interface DmpChatComposerProps {
  projectName: string
  inputRef: RefObject<DmpChatComposerInputHandle>
}

export function DmpChatComposer({ projectName, inputRef }: DmpChatComposerProps) {
  const { isHydrating, isTurnActive, lastUsage, newChat, requestSidebarTab, sessionFiles, sessionFilesLoading } =
    useDmpChatRuntime()
  const { agentKey, agents, agentsLoading } = useDmpChatAgent()
  const { getSkillApiGetSkills, getToolRegistrationApiListTools, getToolsetRegistrationApiListToolsets } =
    useRestClient()
  const { data: availableSkills } = getSkillApiGetSkills.useQuery(undefined)
  const { data: availableTools } = getToolRegistrationApiListTools.useQuery(undefined)
  const { data: availableToolsets } = getToolsetRegistrationApiListToolsets.useQuery(undefined)
  const composerText = useAuiState((state) => state.composer.text)
  const composerAttachments = useAuiState((state) => state.composer.attachments)
  const attachmentErrorMessage = composerAttachments
    .map((attachment) =>
      attachment.status.type === 'incomplete' && attachment.status.reason === 'error'
        ? attachment.status.message
        : undefined
    )
    .find((message): message is string => Boolean(message))
  const hasUnreadyAttachments = composerAttachments.some((attachment) => attachment.status.type !== 'complete')
  const isComposerDisabled = isTurnActive || isHydrating
  const sendDisabled = isComposerDisabled || !composerText.trim() || hasUnreadyAttachments
  const selectedAgentConfig = agents?.find((agent) => agent.config?.key === agentKey)?.config
  const mentionCategories = useMemo(
    () =>
      buildDmpChatMentionCategories({
        agentConfig: selectedAgentConfig,
        sessionFiles,
        skills: availableSkills ?? [],
        tools: availableTools ?? [],
        toolsets: availableToolsets ?? [],
      }),
    [availableSkills, availableTools, availableToolsets, selectedAgentConfig, sessionFiles]
  )
  const mention = unstable_useMentionAdapter({
    categories: mentionCategories,
    fallbackIcon: dmpChatTriggerFallbackIcon,
    iconMap: dmpChatTriggerIconMap,
    includeModelContextTools: false,
  })
  const [commandAnnouncement, setCommandAnnouncement] = useState('')
  const handleSlashCommand = useCallback(
    (commandId: string) => {
      if (isComposerDisabled) {
        return
      }

      const intent = getDmpChatSlashIntent(commandId)
      switch (intent) {
        case 'help':
          setCommandAnnouncement(formatDmpChatSlashHelp())
          return
        case 'new-chat':
          setCommandAnnouncement(
            newChat()
              ? 'New chat started.'
              : 'New chat is unavailable while a Turn is active or the Thread is hydrating.'
          )
          return
        case 'focus-files':
          requestSidebarTab('files')
          setCommandAnnouncement('Opened the session Files tab.')
          return
        default:
          return
      }
    },
    [isComposerDisabled, newChat, requestSidebarTab, setCommandAnnouncement]
  )
  const slashCommands = useMemo(
    () =>
      DMP_CHAT_SLASH_COMMANDS.map((command) => ({
        ...command,
        icon: command.id,
        execute: () => handleSlashCommand(command.id),
      })),
    [handleSlashCommand]
  )
  const slash = unstable_useSlashCommandAdapter({
    commands: slashCommands,
    fallbackIcon: dmpChatTriggerFallbackIcon,
    iconMap: dmpChatTriggerIconMap,
    removeOnExecute: true,
  })
  const isTriggerLoading = sessionFilesLoading || agentsLoading

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-3xl flex-col gap-2">
      {lastUsage && <DmpChatContextUsage isTurnActive={isTurnActive} usage={lastUsage} />}
      <div
        id="dmp-chat-composer-command-status"
        aria-atomic="true"
        aria-live="polite"
        className="min-h-5 whitespace-pre-line px-2 text-xs text-muted-foreground"
        role="status"
      >
        {commandAnnouncement}
      </div>
      <ComposerPrimitive.Unstable_TriggerPopoverRoot>
        <ComposerPrimitive.AttachmentDropzone asChild disabled={isComposerDisabled}>
          <ComposerPrimitive.Root
            aria-label="DMP Chat composer"
            className="relative flex w-full min-w-0 flex-col gap-2 rounded-lg border bg-background p-2"
            onSubmit={(event) => {
              if (sendDisabled) {
                event.preventDefault()
              }
            }}
          >
            <ComposerAttachments />
            {attachmentErrorMessage && (
              <p className="px-2 text-xs text-destructive" role="alert">
                {attachmentErrorMessage}
              </p>
            )}
            <div className="flex min-w-0 items-end gap-2">
              {!isComposerDisabled && <ComposerAddAttachment />}
              <DmpChatLexicalInput
                accessibleName={`Message the Project Agent about ${projectName}`}
                describedBy="dmp-chat-turn-status dmp-chat-composer-command-status"
                className={clsx(
                  'relative min-h-11 min-w-0 flex-1 break-words',
                  '[&_.aui-lexical-input]:min-h-11 [&_.aui-lexical-input]:w-full [&_.aui-lexical-input]:min-w-0',
                  '[&_.aui-lexical-input]:resize-none [&_.aui-lexical-input]:break-words [&_.aui-lexical-input]:border-0',
                  '[&_.aui-lexical-input]:bg-transparent [&_.aui-lexical-input]:px-2 [&_.aui-lexical-input]:py-2',
                  '[&_.aui-lexical-input]:text-sm [&_.aui-lexical-input]:outline-none',
                  '[&_.aui-lexical-placeholder]:pointer-events-none [&_.aui-lexical-placeholder]:absolute',
                  '[&_.aui-lexical-placeholder]:inset-x-0 [&_.aui-lexical-placeholder]:top-0',
                  '[&_.aui-lexical-placeholder]:px-2 [&_.aui-lexical-placeholder]:py-2',
                  '[&_.aui-lexical-placeholder]:text-sm [&_.aui-lexical-placeholder]:text-muted-foreground',
                  isComposerDisabled && 'cursor-not-allowed opacity-60'
                )}
                disabled={isComposerDisabled}
                directiveChip={DmpChatDirectiveChip}
                formatter={unstable_defaultDirectiveFormatter}
                placeholder="Ask the Project Agent... Type @ to mention or / for commands."
                ref={inputRef}
                sendDisabled={sendDisabled}
                submitMode="enter"
              />
              <ComposerTriggerPopover
                char="@"
                {...mention}
                adapter={isComposerDisabled ? undefined : mention.adapter}
                emptyItemsLabel="none yet"
                isLoading={isTriggerLoading}
              />
              <ComposerTriggerPopover char="/" {...slash} adapter={isComposerDisabled ? undefined : slash.adapter} />
              {isTurnActive ? (
                <ComposerPrimitive.Cancel
                  aria-describedby="dmp-chat-turn-status"
                  aria-label="Stop the active Project Agent request"
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  type="button"
                >
                  Stop
                </ComposerPrimitive.Cancel>
              ) : (
                <ComposerPrimitive.Send
                  className={clsx(buttonVariants({ variant: 'default', size: 'icon' }), '!rounded-full')}
                  disabled={sendDisabled}
                  type="submit"
                >
                  <IconV2ArrowLeftOutline className="rotate-90" />
                  <span className="sr-only">Send</span>
                </ComposerPrimitive.Send>
              )}
            </div>
          </ComposerPrimitive.Root>
        </ComposerPrimitive.AttachmentDropzone>
      </ComposerPrimitive.Unstable_TriggerPopoverRoot>
    </div>
  )
}
